/**
 * A live Feather demo run: the presenter's settings, the run this tab started,
 * and the addresses a run uses.
 *
 * Demo-only. Read only by the /feather pages and the OAuth return bounce, all
 * reached through `REVIEW_PAGES` behind `DEMO_SURFACES_IN_BUILD`, so a
 * production build drops this module.
 *
 * Two stores, chosen on purpose:
 *
 * - The RUN (applicant token and Case id) is in sessionStorage. An OAuth
 *   sign-in is a full page load in the same tab, and without the run the
 *   return bounce, the preview tabs and the ATS view would all forget it
 *   mid-demo. This tab only, gone when it closes, never localStorage. The
 *   applicant page already keeps the same token in sessionStorage before every
 *   sign-in (`dv_oauth_return_token`), so nothing new is exposed.
 * - The SETTINGS (applicant name, role, flow) are a per-presenter convenience
 *   with no token and no address in them, so they are in localStorage.
 *
 * Every storage call is wrapped: private browsing can throw, and a demo must
 * still start with the defaults.
 */

const RUN_KEY = "dv_feather_demo_run";
const SETTINGS_KEY = "dv_feather_demo_settings";

export interface FeatherDemoRun {
  /** The applicant session token, as `POST /api/v1/sessions` returned it. */
  token: string;
  inquiry_id: string;
  /** Shared by the run's applicant and suggested voucher addresses. */
  tag: string;
  applicant_email: string;
  voucher_email: string;
  started_at: string;
}

export interface FeatherDemoSettings {
  applicant_name: string;
  role: string;
  /** Empty means "the org's only flow". */
  workflow_key: string;
}

/** Fictional, the same person the sample ATS page shows. */
export const DEFAULT_DEMO_SETTINGS: FeatherDemoSettings = {
  applicant_name: "Alex Morgan",
  role: "Senior Product Designer",
  workflow_key: "",
};

export function readDemoRun(): FeatherDemoRun | null {
  try {
    const raw = sessionStorage.getItem(RUN_KEY);
    if (!raw) return null;
    const run = JSON.parse(raw) as Partial<FeatherDemoRun>;
    if (typeof run.token !== "string" || !run.token || typeof run.inquiry_id !== "string") return null;
    return {
      token: run.token,
      inquiry_id: run.inquiry_id,
      tag: typeof run.tag === "string" ? run.tag : "",
      applicant_email: typeof run.applicant_email === "string" ? run.applicant_email : "",
      voucher_email: typeof run.voucher_email === "string" ? run.voucher_email : "",
      started_at: typeof run.started_at === "string" ? run.started_at : "",
    };
  } catch {
    return null;
  }
}

export function writeDemoRun(run: FeatherDemoRun): void {
  try {
    sessionStorage.setItem(RUN_KEY, JSON.stringify(run));
  } catch {
    /* private mode: the run still works until the first full page load */
  }
}

export function clearDemoRun(): void {
  try {
    sessionStorage.removeItem(RUN_KEY);
  } catch {
    /* nothing to clear */
  }
}

export function readDemoSettings(): FeatherDemoSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_DEMO_SETTINGS };
    const saved = JSON.parse(raw) as Partial<FeatherDemoSettings>;
    const name = typeof saved.applicant_name === "string" ? saved.applicant_name.trim() : "";
    return {
      applicant_name: name || DEFAULT_DEMO_SETTINGS.applicant_name,
      role: typeof saved.role === "string" ? saved.role.trim() : DEFAULT_DEMO_SETTINGS.role,
      workflow_key: typeof saved.workflow_key === "string" ? saved.workflow_key : "",
    };
  } catch {
    return { ...DEFAULT_DEMO_SETTINGS };
  }
}

export function writeDemoSettings(settings: FeatherDemoSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* blocked storage: the defaults apply next time */
  }
}

/** `1013-143205` for 13 October, 14:32:05 local time. Unique per run in practice. */
export function runTag(now: Date = new Date()): string {
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(now.getMonth() + 1)}${two(now.getDate())}-${two(now.getHours())}${two(now.getMinutes())}${two(now.getSeconds())}`;
}

/**
 * `juan@digavouch.com` + `feather-1013-143205` -> `juan+feather-1013-143205@digavouch.com`.
 *
 * Built from whoever is signed in, so the run's emails reach the presenter's
 * own inbox and no name is written here. An existing `+suffix` is replaced,
 * not stacked. Null when the address has no usable local part or domain.
 */
export function plusAddress(email: string | null | undefined, suffix: string): string | null {
  if (!email) return null;
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return null;
  const local = trimmed.slice(0, at).split("+")[0];
  if (!local) return null;
  return `${local}+${suffix}@${trimmed.slice(at + 1)}`;
}

export const applicantAddressFor = (email: string | null | undefined, tag: string) =>
  plusAddress(email, `feather-${tag}`);

export const voucherAddressFor = (email: string | null | undefined, tag: string) =>
  plusAddress(email, `voucher-${tag}`);

/**
 * The voucher token of the vouch request this run sent most recently, read
 * from the invitations the applicant page already holds.
 *
 * Only an open (`pending`) invitation counts, and only one whose link this tab
 * received: the list endpoint never returns links, so a link exists only after
 * an invite or resend in this tab. Latest is `last_resent_at`, else `sent_at`
 * (a resend issues a new link and the old one stops working).
 *
 * `noLinkReason` says why there is nothing to open, for the disabled tab.
 */
export function lastSentVoucher(
  invitations: readonly { status: string; sent_at: string; last_resent_at?: string; voucher_link: string | null }[],
): { token: string; noLinkReason?: undefined } | { token: null; noLinkReason: "none_sent" | "link_not_in_tab" | "vouch_received" } {
  const open = invitations.filter((i) => i.status === "pending");
  let best: { token: string; at: number } | null = null;
  for (const inv of open) {
    const token = voucherTokenFromLink(inv.voucher_link);
    if (!token) continue;
    const at = Date.parse(inv.last_resent_at ?? inv.sent_at);
    const when = Number.isNaN(at) ? 0 : at;
    if (!best || when >= best.at) best = { token, at: when };
  }
  if (best) return { token: best.token };
  if (open.length > 0) return { token: null, noLinkReason: "link_not_in_tab" };
  // Nothing open: say the vouch came back rather than that none was sent.
  if (invitations.some((i) => i.status === "complete")) return { token: null, noLinkReason: "vouch_received" };
  return { token: null, noLinkReason: "none_sent" };
}

/** The `token` query parameter of a voucher link, or null. */
export function voucherTokenFromLink(link: string | null | undefined): string | null {
  if (!link) return null;
  try {
    return new URL(link, "https://placeholder.invalid").searchParams.get("token") || null;
  } catch {
    return null;
  }
}

/**
 * Which flow a run starts on: the saved choice while the org still offers it,
 * else the org's only flow. Null when there is nothing unambiguous to start,
 * and the setup page asks the presenter to pick.
 */
export function pickWorkflowKey(
  saved: string,
  offered: readonly { workflow_key: string }[] | undefined,
): string | null {
  const keys = (offered ?? []).map((w) => w.workflow_key);
  if (saved && keys.includes(saved)) return saved;
  return keys.length === 1 ? keys[0] : null;
}
